import cn from 'classnames';
import { FC, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { LocationStateType } from '@/shared/config';
import {
  useAppSelector,
  useMedia,
  useMobileSimplifiedToothChartVisibility,
} from '@/shared/hooks';
import { scrollToElementByID, trackEvent } from '@/shared/lib';
import {
  Column,
  Layout,
  MobileLayout,
  Spinner,
  WidgetCard,
  WidgetLayout,
} from '@/shared/ui';

import { assetsModel } from '@/entities/assets';
import { ConfirmModal } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { ReportSettingsToggle, reportsModel } from '@/entities/reports';

import { ExtendedConclusion } from '@/features/extendedConclusion';
import { useFDA3DGPRestrictions } from '@/features/fdaRestrictions';
import { MedicalImageToolbar } from '@/features/medicalImageToolbar';
import { MPRModel, useDCCTVolumeSizeHint } from '@/features/MPR';
import { MaskFiltersWrapper, MaskTooltip } from '@/features/renderMasks';
import { useReportCheck } from '@/features/reportCheck';
import { ReportSignatures } from '@/features/reportSignatures';
import { SimplifiedToothChart } from '@/features/simplifiedToothChart';

import { AddConditionModal } from '@/widgets/AddConditionModal';
import { Conclusion } from '@/widgets/Conclusion';
import {
  GpToothCardFullHeadMprSlices,
  useFullHeadMprAvailability,
  useToothBasisSeriesPriority,
  useWholeVolumePrefetch,
} from '@/widgets/FullHeadMPR';
import { GetFullAccessModal } from '@/widgets/GetFullAccessModal';
import { MobileHeader } from '@/widgets/Header';
import { MIRenderModal } from '@/widgets/MIRenderModal';
import { NonDentalFindings } from '@/widgets/NonDentalFindings';
import { PreviewType, ReportActions } from '@/widgets/ReportActions';
import { ReportHeading } from '@/widgets/ReportHeading';
import { ToothCardList } from '@/widgets/ToothCard';

import { GpFDAToothChart } from '@/pages/GpFDAReport/ui/GpToothChart/GpFDAToothChart';

import styles from './GpReport.module.scss';
import { GpToothChart } from './ui/GpToothChart/GpToothChart';
import { PanoReformatRender } from './ui/PanoReformatRender/PanoReformatRender';
import { ToothNumberEditPopup } from './ui/ToothNumberEditPopup';

type ReportProps = {
  className?: string;
};

export const GpReport: FC<ReportProps> = (props) => {
  const { className } = props;

  const { reportID = '', patientID = '' } = useParams();
  const location = useLocation();
  const { isMobile } = useMedia();

  const widgetRef = useRef<HTMLDivElement>(null);

  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const reportLoading = useAppSelector(reportsModel.selectors.selectLoading);
  const showExtendedConclusion = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_CBCTExtendedConclusion,
    ),
  );
  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );
  const dcctVolumeState = useAppSelector(
    MPRModel.selectors.selectDCCTVolumeState({ patientID, reportID }),
  );
  const isDCCTVolumeReady = dcctVolumeState.status === 'ready';

  useDCCTVolumeSizeHint({ patientID, reportID });

  const fullHeadMprAvailability = useFullHeadMprAvailability({
    patientID,
    reportID,
  });

  useToothBasisSeriesPriority(
    activeToothID,
    fullHeadMprAvailability === 'available',
  );

  useWholeVolumePrefetch({
    isEnabled: isDCCTVolumeReady && fullHeadMprAvailability !== 'unavailable',
    volumeFileID: dcctVolumeState.volumeFileID,
  });
  const { shouldApplyToothChartRestrictions } = useFDA3DGPRestrictions();

  useReportCheck(reportID);

  // type casting is necessary for typification location.state
  const locationState = location?.state as LocationStateType;
  const scrollToElementID = locationState?.toothID;
  const lastPositionReport = locationState?.lastPositionReport;
  const isReportLoading =
    reportLoading === 'idle' || reportLoading === 'pending';
  const CBCTPanoramaAsset = useAppSelector(
    assetsModel.selectors.selectGPAssetByReportID(reportID),
  );

  const CBCTPanoramaAssetID = CBCTPanoramaAsset?.ID;
  const mobileToothChartStyle = styles.mobileToothChart;

  const { simplifiedChartRef, shouldShowMobileSimplifiedToothChart } =
    useMobileSimplifiedToothChartVisibility({
      mobileToothChartStyle,
    });

  useEffect(() => {
    if (scrollToElementID) {
      scrollToElementByID(scrollToElementID, 'auto');
    } else if (lastPositionReport) {
      window.scrollTo(0, lastPositionReport);
    }

    trackEvent({
      eventName: 'GpReport Page Viewed',
      properties: {
        patient_id: patientID,
        report_id: reportID,
        report_type: ReportType.ReportType_CBCT_GP,
      },
      isPageViewEvent: true,
    });
  }, []);

  if (isReportLoading) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner className={styles.spinner} size={96} />
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const reportImageLoading = !CBCTPanoramaAssetID;

  const renderFullHeadMprSliceContainer =
    fullHeadMprAvailability !== 'unavailable'
      ? (cardToothID: string) => (
          <GpToothCardFullHeadMprSlices
            toothID={cardToothID}
            isVolumeReady={isDCCTVolumeReady}
          />
        )
      : undefined;

  const conclusion = showExtendedConclusion ? (
    <WidgetCard>
      <ExtendedConclusion />
      <ReportSignatures />
    </WidgetCard>
  ) : (
    <Conclusion />
  );

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader />
        {shouldShowMobileSimplifiedToothChart && (
          <div
            ref={simplifiedChartRef}
            className={styles.simplifiedToothChartWrapper}
          >
            <SimplifiedToothChart
              reportID={reportID}
              patientID={patientID}
              useScroll
              mergePrimaryWithAdult
            />
          </div>
        )}
        <MobileLayout.Main>
          <WidgetCard
            className={styles.mobilePanoImageWidget}
            loading={reportImageLoading}
          >
            <PanoReformatRender>
              <MedicalImageToolbar
                toolbarID="main"
                className={cn(styles.mobileToolbar)}
              />

              <MaskFiltersWrapper className={styles.maskFilters} />

              <MaskTooltip />
            </PanoReformatRender>
          </WidgetCard>

          <ReportSettingsToggle />

          <WidgetCard className={mobileToothChartStyle}>
            {shouldApplyToothChartRestrictions ? (
              <GpFDAToothChart />
            ) : (
              <GpToothChart />
            )}
          </WidgetCard>

          <NonDentalFindings />
          <ToothCardList
            renderImageContainer={renderFullHeadMprSliceContainer}
          />
          {conclusion}
        </MobileLayout.Main>
        <MobileLayout.Footer>
          <ReportActions
            patientID={patientID}
            reportID={reportID}
            previewType={PreviewType.Preview3D}
          />
        </MobileLayout.Footer>

        {/* Modals */}
        <AddConditionModal />
        <MIRenderModal />
        <GetFullAccessModal />
        <ConfirmModal />
        <ToothNumberEditPopup />
      </MobileLayout>
    );
  }

  return (
    <>
      <Layout.Content>
        <Layout.Main>
          <Layout.Content
            className={cn(styles.container, className)}
            data-section="gp-report"
          >
            <WidgetLayout className={styles.columnWrapper}>
              <Column className={styles.rightColumn}>
                <NonDentalFindings />
                <ToothCardList
                  renderImageContainer={renderFullHeadMprSliceContainer}
                />
                {conclusion}
              </Column>
              <Column className={styles.column}>
                <div className={styles.leftFixedColumn}>
                  <ReportHeading
                    report={report}
                    isReportLoading={isReportLoading}
                  />
                  <div ref={widgetRef} className={styles.widgetContainer}>
                    <WidgetCard
                      className={styles.panoImageWidget}
                      loading={reportImageLoading}
                    >
                      <PanoReformatRender>
                        <ToothNumberEditPopup />

                        <MedicalImageToolbar toolbarID="main" />
                        <MaskFiltersWrapper className={styles.maskFilters} />
                        <MaskTooltip />
                      </PanoReformatRender>
                    </WidgetCard>
                  </div>
                  <WidgetCard className={styles.toothChart}>
                    {shouldApplyToothChartRestrictions ? (
                      <GpFDAToothChart />
                    ) : (
                      <GpToothChart />
                    )}
                  </WidgetCard>
                </div>
              </Column>
            </WidgetLayout>
          </Layout.Content>
        </Layout.Main>
      </Layout.Content>

      <Layout.Footer settingsFooter className={styles.footerContainer}>
        <ReportSettingsToggle className={styles.settingsToggle} />

        <ReportActions
          patientID={patientID}
          reportID={reportID}
          className={styles.reportActions}
          previewType={PreviewType.Preview3D}
        />
      </Layout.Footer>

      {/* Modals */}
      <AddConditionModal />
      <MIRenderModal />
      <GetFullAccessModal />
      <ConfirmModal />
    </>
  );
};
